import { useState } from 'react';
import { ChevronDown } from 'lucide-react';

const LANGS = ['pl', 'en', 'ru', 'ua'];
const LANG_LABELS = { pl: 'PL', en: 'EN', ru: 'RU', ua: 'UA' };

/**
 * Parse a value that can be either a plain string (old format) or a {pl, en, ru, ua} object.
 * Returns an object with all 4 languages.
 */
export function parseLangValue(val) {
  if (!val) return { pl: '', en: '', ru: '', ua: '' };
  if (typeof val === 'string') return { pl: val, en: '', ru: '', ua: '' };
  return { pl: val.pl || '', en: val.en || '', ru: val.ru || '', ua: val.ua || '' };
}

/**
 * Get the display value for a specific language, falling back to PL.
 * If value is a plain string (old format, PL only), returns empty for non-PL languages
 * so that i18n fallbacks can kick in.
 */
export function lv(val, lang) {
  if (!val) return '';
  if (typeof val === 'string') return lang === 'pl' ? val : '';
  return val[lang] || val.pl || '';
}

/**
 * Strict version — returns empty string if target language is not filled.
 * Used for page content blocks that should hide when not translated.
 */
export function lvStrict(val, lang) {
  if (!val) return '';
  if (typeof val === 'string') return lang === 'pl' ? val : '';
  return val[lang] || '';
}


/**
 * Multilingual text input for admin forms.
 * Stores/returns a {pl, en, ru, ua} object.
 */
export default function MultiLangField({ value, onChange, rows, placeholder, inputClass }) {
  const [expanded, setExpanded] = useState(false);
  const parsed = parseLangValue(value);
  const isTextarea = rows && rows > 1;

  const handleChange = (lang, text) => {
    const next = { ...parsed, [lang]: text };
    onChange(next);
  };

  const InputEl = isTextarea ? 'textarea' : 'input';
  const extraProps = isTextarea ? { rows } : {};

  return (
    <div className="space-y-2">
      {/* PL - always visible */}
      <div className="relative">
        <span className="absolute top-2.5 right-3 text-[10px] font-bold text-amber-400 uppercase pointer-events-none z-10">PL</span>
        <InputEl
          value={parsed.pl}
          onChange={e => handleChange('pl', e.target.value)}
          placeholder={placeholder}
          className={`${inputClass || ''} ${isTextarea ? 'resize-none' : ''} pr-12`}
          {...extraProps}
        />
      </div>

      {/* Toggle for other languages */}
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-1 text-[10px] text-[#71717A] hover:text-[#A1A1AA] transition-colors"
      >
        <ChevronDown className={`w-3 h-3 transition-transform ${expanded ? 'rotate-180' : ''}`} />
        EN / RU / UA {parsed.en || parsed.ru || parsed.ua ? '(filled)' : '(empty)'}
      </button>

      {expanded && LANGS.filter(l => l !== 'pl').map(lang => (
        <div key={lang} className="relative">
          <span className="absolute top-2.5 right-3 text-[10px] font-bold text-blue-400 uppercase pointer-events-none z-10">{LANG_LABELS[lang]}</span>
          <InputEl
            value={parsed[lang]}
            onChange={e => handleChange(lang, e.target.value)}
            placeholder={`${placeholder || ''} (${LANG_LABELS[lang]})`}
            className={`${inputClass || ''} ${isTextarea ? 'resize-none' : ''} pr-12`}
            {...extraProps}
          />
        </div>
      ))}
    </div>
  );
}
